import { mergeAttributes, Node, ReactNodeViewRenderer } from "@tiptap/react";
import { createElement } from "react";
import type { FieldUiOptions } from "./types";

import { AtomicFieldView } from "./views/atomic-field-view";

/**
 * AtomicField node — structured data fields rendered as React NodeViews.
 *
 * A single node type that renders different interactive components based
 * on the `fieldType` attribute:
 * - "owner": User selection combobox
 * - "enum": Dropdown for predefined options
 * - "text": Simple text input for short values
 *
 * The `value` attribute stores the current field value.
 * The `options` attribute stores enum choices (only used when fieldType is "enum").
 */
export const AtomicField = Node.create({
  name: "atomicField",
  atom: true,
  selectable: true,
  draggable: false,
  defining: true,
  isolating: true,

  addOptions() {
    return {
      HTMLAttributes: {} as Record<string, unknown>,
      fieldUiOptions: {} as FieldUiOptions,
    };
  },

  addAttributes() {
    return {
      fieldId: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-field-id") ?? "",
        renderHTML: (attributes) => ({ "data-field-id": attributes.fieldId }),
      },
      label: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-label") ?? "",
        renderHTML: (attributes) => ({ "data-label": attributes.label }),
      },
      guidance: { default: "", rendered: false },
      ownerId: { default: null, rendered: false },
      fieldType: {
        default: "text",
        parseHTML: (element) => element.getAttribute("data-field-type") ?? "text",
        renderHTML: (attributes) => ({ "data-field-type": attributes.fieldType }),
      },
      options: {
        default: [],
        parseHTML: (element) => {
          try {
            return JSON.parse(element.getAttribute("data-options") ?? "[]");
          } catch {
            return [];
          }
        },
        renderHTML: (attributes) => ({
          "data-options": JSON.stringify(attributes.options),
        }),
      },
      value: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-value") ?? "",
        renderHTML: (attributes) => ({ "data-value": attributes.value }),
      },
      _diffStatus: {
        default: null,
        rendered: false,
      },
      _diffOldValue: {
        default: null,
        rendered: false,
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="atomic-field"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        "data-type": "atomic-field",
      }),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(
      (props) =>
        createElement(AtomicFieldView, {
          ...props,
          fieldUiOptions: this.options.fieldUiOptions,
        }),
      {
        stopEvent: () => true,
      }
    );
  },
});
